import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Form, Spinner, Container, Row, Col, Alert, Badge, ListGroup } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import UserContext from "../../context/UserContext";

const OTApprovalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: contextUser, setUser } = useContext(UserContext);
  
  const [request, setRequest] = useState(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Khóa nút khi đang gửi

  useEffect(() => {
    fetchOTDetail();
  }, [id, contextUser]);

  const fetchOTDetail = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`${API_ENDPOINTS.otRequests}/${id}`);
      const otData = res.data;
      setRequest(otData);

      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const currentLoginId = Number(contextUser?.id || contextUser?.userId || storedUser.id || storedUser.userId);
      
      // Tìm log của bước duyệt hiện tại
      const currentStepLog = otData.approvalLogs?.find(
        (log) => Number(log.stepOrder) === Number(otData.currentStep) && !log.isProcessed
      );

      // logic xác thực: Phải đúng ID người duyệt AND trạng thái đơn phải là Pending (1)
      const hasPermission = currentStepLog && 
                            Number(currentStepLog.approverId) === currentLoginId && 
                            Number(otData.status) === 1;

      setIsAuthorized(hasPermission);

    } catch (err) {
      console.error("Error fetching detail:", err);
      navigate("/ot-approval");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (isApprove) => {
    if (isSubmitting) return; // Chống spam click
    
    const action = isApprove ? "approve" : "reject";
    setIsSubmitting(true);

    try {
      const finalComment = comment.trim() || (isApprove ? "Approved" : "Rejected");
      await axiosClient.post(`${API_ENDPOINTS.otRequests}/${id}/${action}`, 
        JSON.stringify(finalComment), 
        { headers: { "Content-Type": "application/json" } }
      );
      alert(`Request ${action}ed successfully!`);
      navigate("/ot-approval");
    } catch (err) {
      alert(err.response?.data?.message || "An error occurred during approval. Redirecting to login...");
      
      // Khi xảy ra lỗi (ví dụ: Token hết hạn, mất quyền session), dọn dẹp bộ nhớ và đá về trang Login
      localStorage.clear();
      if (typeof setUser === "function") {
        setUser(null);
      }
      navigate("/login", { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hàm helper format đầy đủ Ngày và Giờ (DD/MM/YYYY, HH:mm) để hiển thị khoảng thời gian tường minh
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "--/--/---- --:--";
    const date = new Date(dateStr);
    const d = date.toLocaleDateString("en-GB"); // DD/MM/YYYY
    const t = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); // HH:mm
    return `${d} ${t}`;
  };

  if (loading || !request) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <Container className="py-5">
      <Card className="shadow-lg border-0 rounded-4 overflow-hidden mx-auto" style={{ maxWidth: "750px" }}>
        <Card.Header className="bg-dark text-white py-3 d-flex justify-content-between align-items-center px-4">
          <h5 className="mb-0 fw-bold text-uppercase">OT Request Details</h5>
          <Badge bg={Number(request.status) === 2 ? "success" : Number(request.status) === 3 ? "danger" : "warning"} className="px-3 py-2">
            {Number(request.status) === 1 ? "PENDING" : Number(request.status) === 2 ? "APPROVED" : Number(request.status) === 3 ? "REJECTED" : "DRAFT"}
          </Badge>
        </Card.Header>

        <Card.Body className="p-4">
          {!isAuthorized && Number(request.status) === 1 && (
            <Alert variant="warning" className="mb-4 small fw-bold shadow-sm">
              <i className="fas fa-exclamation-triangle me-2"></i>Read-only: You do not have permission to approve at this current step.
            </Alert>
          )}

          <div className="mb-4">
            <label className="text-muted small fw-bold text-uppercase mb-2 d-block">Requested By</label>
            <div className="d-flex align-items-center p-3 bg-light rounded-3 border">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: "45px", height: "45px" }}>
                <i className="fas fa-user-tie"></i>
              </div>
              <div>
                <div className="fw-bold text-dark fs-5">{request.user?.employeeLeaveDetail?.fullName || "Staff"}</div>
                <div className="text-muted small">Employee ID: #{request.userId}</div>
              </div>
            </div>
          </div>

          <Row className="mb-4 g-3">
            <Col xs={12}>
              <div className="p-3 border rounded-3 bg-white shadow-sm">
                <label className="text-muted small fw-bold text-uppercase d-block mb-1">Work Title</label>
                <div className="fw-bold text-dark">{request.title}</div>
              </div>
            </Col>
            
            {/* 💡 ĐÃ ĐIỀU CHỈNH: Hiển thị chi tiết mốc thời gian Từ Ngày Giờ -> Đến Ngày Giờ rõ ràng */}
            <Col xs={8}>
              <div className="p-3 border rounded-3 bg-white shadow-sm h-100">
                <label className="text-muted small fw-bold text-uppercase d-block mb-1">Time Range</label>
                <div className="text-secondary small">
                  <div className="mb-1">
                    <span className="fw-bold text-dark me-1">From:</span>
                    <span className="text-primary fw-bold">{formatDateTime(request.startTime)}</span>
                  </div>
                  <div>
                    <span className="fw-bold text-dark me-1">To:</span>
                    <span className="text-primary fw-bold">{formatDateTime(request.endTime)}</span>
                  </div>
                </div>
              </div>
            </Col>
            
            <Col xs={4}>
              <div className="p-3 border rounded-3 bg-white shadow-sm h-100 d-flex flex-column justify-content-center">
                <label className="text-muted small fw-bold text-uppercase d-block mb-1">Total Hours</label>
                <div className="text-danger fw-bold fs-5">{request.totalHours} Hr(s)</div>
              </div>
            </Col>
          </Row>

          <div className="mb-4">
            <label className="text-muted small fw-bold text-uppercase mb-2 d-block">Content / Reason</label>
            <div className="p-3 border rounded-3 bg-light text-dark" style={{ minHeight: "80px" }}>{request.content}</div>
          </div>

          <div className="mb-4">
            <label className="text-muted small fw-bold text-uppercase mb-2 d-block">Approval Progress</label>
            <ListGroup variant="flush" className="border rounded-3 shadow-sm">
              {request.approvalLogs?.map((log, index) => (
                <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center py-2">
                  <div className="small">
                    <span className={`fw-bold ${Number(log.stepOrder) === Number(request.currentStep) ? "text-primary" : "text-muted"}`}>
                      Step {log.stepOrder}:
                    </span> {log.approver?.employeeLeaveDetail?.fullName}
                  </div>
                  <Badge bg={log.status === "Approved" ? "success" : log.status === "Rejected" ? "danger" : "light"} text={log.status === "Pending" ? "dark" : "white"} pill className="border">
                    {log.status || "Pending"}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>

          {isAuthorized && (
            <>
              <Form.Group className="mb-4">
                <Form.Label className="small fw-bold text-muted text-uppercase">Approver's Comment</Form.Label>
                <Form.Control as="textarea" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} className="border-primary" />
              </Form.Group>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" className="px-4" onClick={() => navigate("/ot-approval")} disabled={isSubmitting}>BACK</Button>
                <Button variant="danger" className="flex-grow-1 fw-bold" onClick={() => handleAction(false)} disabled={isSubmitting}>
                  {isSubmitting ? <Spinner size="sm" animation="border" /> : "REJECT"}
                </Button>
                <Button variant="success" className="flex-grow-1 fw-bold" onClick={() => handleAction(true)} disabled={isSubmitting}>
                  {isSubmitting ? <Spinner size="sm" animation="border" /> : "APPROVE"}
                </Button>
              </div>
            </>
          )}

          {!isAuthorized && (
             <Button variant="secondary" className="w-100 fw-bold" onClick={() => navigate("/ot-approval")}>CLOSE AND BACK</Button>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default OTApprovalDetail;